import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatInput } from '@angular/material';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subject } from 'rxjs';
import { AssessmentService } from 'src/app/core/api/assessment.service';
import { isWithinTimeFrameInDays } from 'src/app/core/api/date.util';
import { PatientService } from 'src/app/core/api/patient.service';
import { AssessmentFormService } from 'src/app/encounters/assessment-form.service';
import { AssessmentRouteData } from 'src/app/encounters/assessment/assessment.component';
import { medicalHistoryCustomAutopopulateConfig } from 'src/app/encounters/encounter-linking/medical-history-linking';
import { MedicalHistoryLayoutService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/medical-history-layout/medical-history-layout.service';
import { AutopopulateService } from 'src/app/shared/autopopulate/autopopulate.service';
import { DryEyeMedicalHistoryFormGroup } from '../dry-eye-medical-history-form.model';
import { Assessment, Drug, IntakeForm, QuestionnaireRequest } from './../../../../../../API';
import { PatientFileService } from './../../../../../patients/patient/patient-file/patient-file.service';
import { DerfsService } from './../../../../../questionnaires/derfs/derfs.service';
import { SchemaService } from './../../../../symptoms/services/schema/schema.service';

@Component({
  selector: 'csi-medical-history-fields',
  templateUrl: './medical-history-fields.component.html',
  styleUrls: ['./medical-history-fields.component.css']
})
export class MedicalHistoryFieldsComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('datePickerInput', { static: false }) datePickerInputRef: MatInput;
  @Input() formGroup: DryEyeMedicalHistoryFormGroup;
  @Input() medicalHistoryApplicableFields: string[];
  @Input() assessmentMethod: string;
  @Input() assessmentRouteData: AssessmentRouteData;
  @Input() onPatientFileSaved$: Subject<Object>;
  @Input() onQuestionnaireLinked$: Subject<QuestionnaireRequest[]>;
  @Input() isMedicationSection: boolean;
  @Input() questionnaireMode: boolean;
  @Input() isLocked: boolean;

  readonly disabledFieldsDisclaimer = `The disabled fields are non-modifiable data. This information is populated based on the response by the patients when they fill out CDERFS/DERFS and Intake Form. You can change the information through the patient profile.`;

  readonly eyeTearingQuantityBottomLabels = [
    'N/A',
    'NO TEARS AT ALL',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'FALLS ON CHEEK ALL THE TIME'
  ];

  readonly cityHumidityLevelBottomLabels = [
    'VERY DRY',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'VERY HUMID'
  ];

  readonly cityPollutionLevelBottomLabels = [
    'NO POLLUTION',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'VERY POLLUTED'
  ];

  readonly sensitivityToLightBottomLabels = ['NONE', '', '', '', '', '', '', '', '', '', 'SEVERE'];
  readonly sensitivityToWindBottomLabels = ['NONE', '', '', '', '', '', '', '', '', '', 'SEVERE'];
  readonly impactOfSymptomsOnMoodBottomLabels = [
    'NONE',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'SEVERE'
  ];
  readonly impactOfSymptomsOnLifeBottomLabels = [
    'NONE',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'SEVERE'
  ];

  readonly eyeItchinessQuantityBottomLabels = [
    'NONE',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'SEVERE'
  ];

  readonly frequencyOfSymptomsBottomLabels = [
    'N/A',
    'RARELY',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'ALL THE TIME'
  ];

  readonly severityOfSymptomsBottomLabels = [
    'N/A',
    'VERY MILD',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'VERY SEVERE'
  ];

  readonly qualityBottomLabels = ['N/A', 'POOR', '', '', '', '', '', '', '', '', 'GOOD'];

  readonly stressLevelBottomLabels = ['N/A', 'LOW', '', '', '', '', '', '', '', '', 'HIGH'];

  public id: string;
  public nonModifiableData;
  public disabledNonModifiableFields = new Set();
  public assessment: Assessment;
  public lastCompletedDerfs = null;
  public drugsNotCopied: string[] = [];
  public drugsNotFound: string[] = [];
  public dateToday: Date = new Date();

  public ngSelectMedicalHistoryFieldKeys = {
    tearingQuality: 'tearingQuality',
    screenTime: 'screenTime',
    bothersomeEye: 'bothersomeEye',
    symptomSeverityOccurrence: 'symptomSeverityOccurrence',
    sleepingPosition: 'sleepingPosition',
    eyeRubbingFrequency: 'eyeRubbingFrequency',
    sunTanExposureFrequency: 'sunTanExposureFrequency',
    lastSunExposure: 'lastSunExposure',
    eyeColor: 'eyeColor',
    longSunExposure: 'longSunExposure',
    naturalHairColor: 'naturalHairColor',
    darkerAfterSunExposure: 'darkerAfterSunExposure',
    skinColor: 'skinColor',
    faceSunSensitivity: 'faceSunSensitivity',
    freckles: 'freckles',
    tanIntensity: 'tanIntensity',
    wearsGlasses: 'wearsGlasses'
  };

  medicationMap = {};

  public medicalHistoryCustomAutopopulateConfig = medicalHistoryCustomAutopopulateConfig;

  constructor(
    public schemaService: SchemaService,
    public medicalHistoryLayoutService: MedicalHistoryLayoutService,
    public patientService: PatientService,
    public assessmentFormService: AssessmentFormService,
    public derfsService: DerfsService,
    public patientFileService: PatientFileService,
    private assessmentService: AssessmentService,
    private autopopulateService: AutopopulateService
  ) {
    this.updateAssessmentId();
  }

  ngOnInit() {}

  ngOnDestroy(): void {}

  ngAfterViewInit(): void {
    this.assessment = this.assessmentRouteData.assessment;

    if (this.isMedicationSection) {
      return;
    }
    this.onPatientFileSaved$.pipe(untilDestroyed(this)).subscribe(patientFileSaveData => {
      if (patientFileSaveData) {
        this.updateAssessmentId();
        this.assessmentService.getAssessment(this.id).subscribe(updatedAssessment => {
          if (this.id === 'new' || this.isToday(new Date(updatedAssessment.createdAt))) {
            this.populateMedicalHistoryWithNonModifiableData(patientFileSaveData);
            this.disableNonModfiableControls();
          }
        });
      }
    });

    this.onQuestionnaireLinked$.subscribe(linkedQuestionnaires => {
      this.patchMedicalHistoryWithLinkedQuestionnairesData(linkedQuestionnaires);
    });

    setTimeout(() => {
      if (this.id === 'new') {
        this.patientService.getPatientQuestionnaire().subscribe(() => {
          this.lastCompletedDerfs = this.getLastCompletedDerfs();
          this.autoFillMedicalHistory();
          this.populateMedicalHistoryWithIntakeForm(this.patientService.intakeForms[0]);

          this.populateMedicationQuestionnaireData();
        });
      } else if (this.assessment && this.isToday(new Date(this.assessment.createdAt))) {
        // this.patientService.getPatient(this.patientService.patient.id).subscribe(patient => {
        //   this.populateMedicalHistoryWithNonModifiableData(JSON.parse(patient.nonModifiableData));
        // });
        this.populateMedicalHistoryWithNonModifiableData(
          JSON.parse(this.patientService.patient.nonModifiableData)
        );
      }
      if (this.datePickerInputRef) {
        this.datePickerInputRef.value = new Date().toISOString();
      }

      this.disableNonModfiableControls();
    }, 1000);
  }

  public updateAssessmentId() {
    const pathname = location.pathname;
    this.id = pathname.substring(pathname.indexOf(':') + 1, pathname.length - 1);
  }

  public isToday(date: Date) {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  public autoFillMedicalHistory() {
    this.applyFunctionOnDerfsSchema(this.mapDerfsToMedicalHistory.bind(this));
    this.patientService.getPatient(this.patientService.patient.id).subscribe(patient => {
      this.populateMedicalHistoryWithNonModifiableData(JSON.parse(patient.nonModifiableData));
    });
  }

  public onPatientFileModified(nonModifiableData) {
    this.populateMedicalHistoryWithNonModifiableData(nonModifiableData);
  }

  public populateMedicalHistoryWithNonModifiableData(nonModifiableData: any) {
    if (!nonModifiableData) return;

    this.formGroup.controls['previousOcularSurgeryFreeText'].patchValue('');
    Object.keys(nonModifiableData).forEach(key => {
      if (this.derfsService.flattenedDerfsSchema[key]) {
        if (
          this.derfsService.flattenedDerfsSchema[key].mapsTo &&
          this.derfsService.flattenedDerfsSchema[key].mapsTo[0] === 'previousOcularSurgeryFreeText'
        ) {
          this.formGroup.controls['previousOcularSurgeryFreeText'].setValue(
            this.patientFileService.transformPastOcularHistoryValue(
              this.formGroup.controls['previousOcularSurgeryFreeText'].value,
              this.derfsService.flattenedDerfsSchema[key].description,
              nonModifiableData[key]
            )
          );
        } else if (this.formGroup['controls'][key]) {
          this.mapDerfsToMedicalHistory(
            this.derfsService.flattenedDerfsSchema[key],
            nonModifiableData[key],
            key
          );
        }
        if (
          // Hacky way to deal with toggleable fields
          this.derfsService.flattenedDerfsSchema[key].mapsTo &&
          this.derfsService.flattenedDerfsSchema[key].mapsTo[1]
        ) {
          this.formGroup['controls'][
            this.derfsService.flattenedDerfsSchema[key].mapsTo[1]
          ].patchValue(nonModifiableData[key + 'Value']);
        }
      } else {
        // console.log('FormControl key not found in flattened derfs schema: ', key);
      }
    });
    this.formGroup.markAsDirty();
  }

  populateMedicalHistoryWithIntakeForm(intakeForm: IntakeForm) {
    if (!intakeForm || !isWithinTimeFrameInDays(new Date(intakeForm.createdAt), new Date(), 365))
      // Only populate with intake form if it was completed within 1 year
      return;
    const response = JSON.parse(intakeForm.response);
    if (intakeForm.config) {
      this.autopopulateService.populateFormGroup(
        this.formGroup,
        this.medicalHistoryCustomAutopopulateConfig,
        response['answers'],
        false
      );
    } else {
      const { patientGeneralHealthQuestion } = response;

      const intakeFormMedications = patientGeneralHealthQuestion.medications;

      if (patientGeneralHealthQuestion && intakeFormMedications) {
        this.formGroup.controls.patientEnteredMedicationsFreeText.setValue(intakeFormMedications);
      }
    }
  }

  public disableNonModfiableControls() {
    // this.applyFunctionOnDerfsSchema(this.disableControlIfNonModifiable.bind(this));
    Object.values(this.derfsService.flattenedDerfsSchema).forEach(controlSchemaData => {
      this.disableControlIfNonModifiable(controlSchemaData);
    });
  }

  public applyFunctionOnDerfsSchema(func: (schemaData, responseDataValue) => void) {
    if (!this.lastCompletedDerfs) return;
    const answers = JSON.parse(this.lastCompletedDerfs['response']['answers']);

    Object.keys(this.derfsService.derfsFormGroupSchema).forEach(questionGroup => {
      if (answers[questionGroup]) {
        Object.keys(this.derfsService.derfsFormGroupSchema[questionGroup].controls).forEach(key => {
          const questionGroupSchema = this.derfsService.derfsFormGroupSchema[questionGroup]
            .controls;

          if (questionGroupSchema[key].type === 'FormGroup') {
            Object.keys(questionGroupSchema[key].controls).forEach(childKey => {
              const controls = questionGroupSchema[key].controls;

              func(controls[childKey], answers[questionGroup][childKey]);

              // For checkboxes
              if (controls[childKey].type === 'SubFormGroup') {
                Object.keys(controls[childKey].controls).forEach(element => {
                  const childControls = controls[childKey].controls;
                  func(childControls[element], answers[questionGroup][element]);
                  func(childControls[element], answers[questionGroup][element + 'Value']);
                });
              }
            });
            if (!questionGroupSchema[key].noParentControl) {
              func(questionGroupSchema[key], answers[questionGroup][key]);
            }
          } else {
            func(questionGroupSchema[key], answers[questionGroup][key]);
          }
        });
      }
    });
  }

  public disableControlIfNonModifiable(schemaData: any) {
    if (schemaData.mapConditions === undefined) {
      return;
    }
    const mapConditionsLength = schemaData.mapConditions.length;
    for (let i = 0; i < mapConditionsLength; i++) {
      if (schemaData.nonModifiableConfig && schemaData.nonModifiableConfig.nonModifiable) {
        if (this.formGroup.controls[schemaData.mapsTo[i]]) {
          // this.formGroup.controls[schemaData.mapsTo[i]].disable();
          this.disabledNonModifiableFields.add(schemaData.mapsTo[i]);
          // console.log('Disabling: ', schemaData.mapsTo[i]);
        }
      }
    }
  }

  public mapDerfsToMedicalHistory(schemaData: any, responseDataValue: any, key?: string) {
    if (schemaData.mapConditions === undefined) {
      return;
    }

    const mapConditionsLength = schemaData.mapConditions.length;
    for (let i = 0; i < mapConditionsLength; i++) {
      // this.disableControlIfNonModifiable(schemaData, i);
      if (
        key &&
        this.patientFileService.flattenedPatientFileSchema[key] &&
        this.patientFileService.flattenedPatientFileSchema[key].isCheckbox
      ) {
        // Directly maps checkboxes to checkboxes, bypassing map conditions to handle unchecking a checkbox.
        this.applyMapping(schemaData, i, responseDataValue);
      } else if (this.mapConditionSatisfied(schemaData.mapConditions[i], responseDataValue)) {
        this.applyMapping(schemaData, i, responseDataValue);
      } else {
        if (schemaData.defaultMapValue) {
          this.formGroup.controls[schemaData.mapsTo[i]].setValue(schemaData.defaultMapValue[i]);
        }
      }
    }
  }

  // public disableControlIfNonModifiable(schemaData: any, mapIndex: number) {
  //   if (schemaData.nonModifiableConfig && schemaData.nonModifiableConfig.nonModifiable) {
  //     this.formGroup.controls[schemaData.mapsTo[mapIndex]].disable();
  //   }
  // }

  mapConditionSatisfied(mapCondition: any, val: any) {
    return (
      val !== undefined &&
      (val === mapCondition ||
        (typeof val === 'number' && val >= mapCondition) ||
        (mapCondition === '' && (typeof val === 'string' || Array.isArray(val)) && val.length >= 1))
    );
  }

  applyMapping(schemaData: any, mapConditionIndex: number, responseDataValue: any) {
    if (this.formGroup.controls[schemaData.mapsTo[mapConditionIndex]]) {
      if (schemaData.mapValues) {
        this.formGroup.controls[schemaData.mapsTo[mapConditionIndex]].setValue(
          schemaData.mapValues[mapConditionIndex]
        );
      } else if (schemaData.transformValue) {
        this.formGroup.controls[schemaData.mapsTo[mapConditionIndex]].setValue(
          schemaData.transformValue(
            responseDataValue,
            this.formGroup.controls[schemaData.mapsTo[mapConditionIndex]].value
          )
        );
      } else {
        this.formGroup.controls[schemaData.mapsTo[mapConditionIndex]].setValue(responseDataValue);
      }
    }
  }

  public patchMedicalHistoryWithLinkedQuestionnairesData(
    linkedQuestionnaires: QuestionnaireRequest[]
  ) {
    let latestDerfsQuestionnaire = null;
    if (!linkedQuestionnaires) return;
    linkedQuestionnaires.forEach(questionnaire => {
      if (questionnaire.type === 'DERFS' || questionnaire.type === 'CDERFS') {
        if (latestDerfsQuestionnaire) {
          if (
            new Date(latestDerfsQuestionnaire.completedAt).getTime() <
            new Date(questionnaire.completedAt).getTime()
          ) {
            latestDerfsQuestionnaire = questionnaire;
          }
        } else {
          latestDerfsQuestionnaire = questionnaire;
        }
      } else if (
        questionnaire.type === 'CUSTOM' ||
        questionnaire.type === 'FITZPATRICK' ||
        questionnaire.type === 'MEDICATION'
      ) {
        this.populateMedicalHistoryWithCustomQuestionnaireData(questionnaire);
      } else if (questionnaire.type === 'SPEED') {
        const existingMedications: Drug[] = this.formGroup.controls.medications.value || [];
        const parsedAnswers = JSON.parse(questionnaire.response.answers);

        let patientEnterMedications = parsedAnswers['SPEED'].patientEnteredMedications;

        if (typeof patientEnterMedications === 'string') {
          patientEnterMedications = patientEnterMedications.split(',');
          (patientEnterMedications as string[]).reduce(
            (acc, current) => [...acc, ...current.split('\n')],
            []
          );
        }

        const processedEntries = patientEnterMedications.map(entry =>
          typeof entry === 'string' ? this.createDrugFromString(entry) : entry
        );

        if (processedEntries.length) {
          this.formGroup.controls.patientEnteredMedicationsFreeText.patchValue([
            ...existingMedications,
            ...processedEntries
          ]);
          this.formGroup.markAsDirty();
        }
      }
      this.populateMedicationQuestionnaireData();
    });
    if (latestDerfsQuestionnaire) {
      const derfsNormalizedValue = this.derfsService.getDerfDataToCopy(latestDerfsQuestionnaire);
      this.formGroup.patchValue({
        ...derfsNormalizedValue,
        patientEnteredMedicationsFreeText: [
          ...new Set([
            ...(this.formGroup.controls.patientEnteredMedicationsFreeText.value || []),
            ...(derfsNormalizedValue.patientEnteredMedicationsFreeText || [])
          ])
        ]
      });
      this.populateMedicationQuestionnaireData();
    }
    this.formGroup.markAsDirty();
  }

  populateMedicalHistoryWithCustomQuestionnaireData(questionnaire: QuestionnaireRequest) {
    this.autopopulateService.populateFormGroup(
      this.formGroup,
      this.medicalHistoryCustomAutopopulateConfig,
      JSON.parse(questionnaire.response['answers']),
      false
    );
    this.populateMedicationQuestionnaireData();
  }

  populateMedicationQuestionnaireData() {
    const existingMedications: Drug[] = this.formGroup.controls.medications.value || [];
    const rawValue = this.formGroup.controls.patientEnteredMedicationsFreeText.value;

    let rawEntries: any[] = [];
    if (typeof rawValue === 'string') {
      rawEntries = rawValue
        .split(/[\n,]/)
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0);
    } else if (Array.isArray(rawValue)) {
      rawEntries = rawValue;
    }

    const processedEntries = rawEntries
      .map(entry => (typeof entry === 'string' ? this.createDrugFromString(entry) : entry))
      .filter(entry => entry !== null && entry.name && entry.name.trim());

    const newMedications = processedEntries.filter(
      (medication: Drug) =>
        !existingMedications.some(
          existing => existing.id === medication.id || existing.name === medication.name
        )
    );

    if (newMedications.length) {
      this.formGroup.controls.medications.patchValue([...existingMedications, ...newMedications]);
      this.formGroup.markAsDirty();
    }
  }

  private createDrugFromString(name: string): Drug {
    return {
      __typename: 'Drug',
      id: null,
      name: name,
      active_ingredients: [
        {
          __typename: 'DrugActiveIngredients',
          ingredient: 'Ingredient not specified',
          strength: null,
          strength_unit: ''
        }
      ],
      routes: [
        {
          __typename: 'DrugRoute',
          route_of_administration: 'Route not specified'
        }
      ],
      dosage_form: 'Dosage form not specified'
    };
  }

  getLastCompletedDerfs() {
    const derfsQuestionnaires = this.patientService.questionnaires.filter(questionnaire => {
      return (
        (questionnaire.type === 'DERFS' || questionnaire.type === 'CDERFS') &&
        questionnaire.completedAt
      );
    });

    const mostRecentDate = new Date(
      Math.max.apply(
        null,
        derfsQuestionnaires.map(item => {
          return item.completedAt && new Date(item.completedAt);
        })
      )
    );

    const mostRecentObject = derfsQuestionnaires.filter(item => {
      if (!item.completedAt) return;
      const d = new Date(item.completedAt);
      return d.getTime() === mostRecentDate.getTime();
    })[0];
    return mostRecentObject;
  }

  public formatThumbLabel(value: number | null): number {
    if (!value) {
      return 0;
    }

    return Math.round(value / 10);
  }

  public closeDatePicker(event: any, dp: any, input: any, control: FormControl) {
    const formattedDate = event
      .toISOString()
      .split('-')
      .join('/')
      .substr(0, 7);
    control.setValue(formattedDate);

    input.value = formattedDate;

    dp.close();
  }

  copyMedicationToFormGroup(medication: Drug) {
    if (
      !(this.formGroup.controls.medications.value as Drug[]).find(
        currentMedication => medication.id === currentMedication.id
      )
    ) {
      this.formGroup.controls.medications.setValue([
        ...(this.formGroup.controls.medications.value || []),
        medication
      ]);
    }
  }
}
